import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface PaymentReceiptProps {
  payment: {
    id: string;
    plan: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    paystack_reference: string | null;
    subscription_expires_at: string | null;
    subscription_period: string | null;
  };
  userEmail?: string;
  userName?: string;
}

export const PaymentReceipt = ({ payment, userEmail, userName }: PaymentReceiptProps) => {
  const handleDownloadReceipt = () => {
    const receiptContent = generateReceiptHTML();
    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${payment.paystack_reference || payment.id.slice(0, 8)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrintReceipt = () => {
    const receiptContent = generateReceiptHTML();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(receiptContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const generateReceiptHTML = () => {
    const paymentDate = format(new Date(payment.created_at), 'MMMM dd, yyyy HH:mm');
    const expiryDate = payment.subscription_expires_at 
      ? format(new Date(payment.subscription_expires_at), 'MMMM dd, yyyy')
      : 'N/A';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Payment Receipt - Bravax Forms</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; margin: 40px; }
          .receipt { max-width: 800px; margin: 0 auto; border: 2px solid #e9ecef; border-radius: 12px; padding: 40px; }
          .header { text-align: center; border-bottom: 3px solid #667eea; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #667eea; margin: 0; font-size: 32px; }
          .status-badge { display: inline-block; background-color: #28a745; color: white; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: bold; margin: 10px 0; }
          .receipt-details { margin: 30px 0; }
          .receipt-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #dee2e6; }
          .receipt-row:last-child { border-bottom: none; }
          .label { color: #6c757d; font-weight: 500; }
          .value { color: #212529; font-weight: 600; }
          .total-row { font-size: 20px; color: #667eea; font-weight: bold; margin-top: 20px; padding-top: 20px; border-top: 3px solid #e9ecef; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #e9ecef; color: #6c757d; font-size: 14px; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>BRAVAX FORMS</h1>
            <p style="margin: 10px 0; color: #6c757d;">Payment Receipt</p>
            <div class="status-badge">✓ PAID</div>
          </div>

          <div class="receipt-details">
            <div class="receipt-row">
              <span class="label">Receipt Number:</span>
              <span class="value">#${payment.paystack_reference || (payment.id || "").slice(0, 8).toUpperCase()}</span>
            </div>
            
            <div class="receipt-row">
              <span class="label">Date Issued:</span>
              <span class="value">${paymentDate}</span>
            </div>
            
            ${userEmail ? `
            <div class="receipt-row">
              <span class="label">Billed To:</span>
              <span class="value">${userName || 'Customer'} (${userEmail})</span>
            </div>
            ` : ''}
            
            <div class="receipt-row">
              <span class="label">Plan:</span>
              <span class="value">${payment.plan} Plan</span>
            </div>
            
            <div class="receipt-row">
              <span class="label">Billing Period:</span>
              <span class="value">${payment.subscription_period || 'Monthly'}</span>
            </div>
            
            <div class="receipt-row">
              <span class="label">Valid Until:</span>
              <span class="value">${expiryDate}</span>
            </div>
            
            <div class="receipt-row">
              <span class="label">Payment Method:</span>
              <span class="value">Paystack</span>
            </div>
            
            <div class="receipt-row">
              <span class="label">Transaction ID:</span>
              <span class="value">${payment.paystack_reference || payment.id}</span>
            </div>
            
            <div class="receipt-row total-row">
              <span class="label">Total Amount Paid:</span>
              <span class="value">${payment.currency} ${Number(payment.amount).toLocaleString()}</span>
            </div>
          </div>

          <div class="footer">
            <p><strong>Bravax Forms</strong></p>
            <p style="margin: 5px 0;">Thank you for your payment!</p>
            <p style="margin: 5px 0; font-size: 12px;">This receipt was generated on ${format(new Date(), 'MMMM dd, yyyy')}</p>
            <p style="margin: 15px 0 0 0; font-size: 12px;">© 2025 Bravax Forms. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const paymentDate = format(new Date(payment.created_at), 'MMM dd, yyyy HH:mm');
  const expiryDate = payment.subscription_expires_at 
    ? format(new Date(payment.subscription_expires_at), 'MMM dd, yyyy')
    : 'N/A';

  return (
    <Card className="card-elegant">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success" />
              Payment Receipt
            </CardTitle>
            <CardDescription>Receipt #{payment.paystack_reference || payment.id.slice(0, 8)}</CardDescription>
          </div>
          <Badge variant={payment.status === 'success' ? 'default' : 'secondary'}>
            {payment.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Transaction ID</span>
            <span className="text-sm font-medium">{payment.paystack_reference || payment.id.slice(0, 8)}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Date</span>
            <span className="text-sm font-medium">{paymentDate}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Plan</span>
            <span className="text-sm font-medium">{payment.plan} Plan</span>
          </div>
          
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Billing Period</span>
            <span className="text-sm font-medium">{payment.subscription_period || 'Monthly'}</span>
          </div>
          
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Valid Until</span>
            <span className="text-sm font-medium">{expiryDate}</span>
          </div>
          
          <div className="flex justify-between py-3 border-t-2 border-border mt-4">
            <span className="text-base font-semibold">Amount Paid</span>
            <span className="text-lg font-bold text-primary">
              {payment.currency} {Number(payment.amount).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={handleDownloadReceipt} className="flex-1" variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Download Receipt
          </Button>
          <Button onClick={handlePrintReceipt} className="flex-1">
            Print Receipt
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
